// The mascot head's "Hi" under the top-bar assistant button's top-right corner. Once per browser
// session, about a second after the 3D head appears, a small brand-gradient "Hi" pill pops out (tail
// toward the head) while the head says hi and nods; the button gives one soft ring pulse. It fades by
// itself after a few seconds or when the assistant opens. Clicks pass through to the button. If the 3D
// head never appears (no WebGL), the 2D bot greets instead after a longer wait.
import { t } from "@ady/i18n";
import { useEffect, useState } from "react";
import { endGreeting, startGreeting, useGreeting } from "./greeting";

const SEEN_KEY = "ady.mascotHello";
const SHOW_FOR_MS = 5000;

function seen(): boolean {
  try {
    return sessionStorage.getItem(SEEN_KEY) === "1";
  } catch {
    return false;
  }
}

function markSeen() {
  try {
    sessionStorage.setItem(SEEN_KEY, "1");
  } catch {
    // storage blocked: the chip may show again next load, which is harmless
  }
}

type Phase = "wait" | "shown" | "leaving" | "gone";

export function AssistantHello({ suppressed }: { suppressed: boolean }) {
  const { headReady } = useGreeting();
  const [phase, setPhase] = useState<Phase>(() => (seen() ? "gone" : "wait"));

  useEffect(() => {
    if (phase !== "wait" || suppressed) return;
    const id = window.setTimeout(
      () => {
        markSeen();
        setPhase("shown");
        startGreeting();
      },
      headReady ? 900 : 6000,
    );
    return () => window.clearTimeout(id);
  }, [phase, headReady, suppressed]);

  useEffect(() => {
    if (phase !== "shown") return;
    const id = window.setTimeout(() => setPhase("leaving"), SHOW_FOR_MS);
    return () => window.clearTimeout(id);
  }, [phase]);

  useEffect(() => {
    if (suppressed && phase === "shown") setPhase("leaving");
    if (suppressed && phase === "wait") setPhase("gone");
  }, [suppressed, phase]);

  useEffect(() => {
    if (phase !== "leaving") return;
    endGreeting();
    const id = window.setTimeout(() => setPhase("gone"), 230);
    return () => window.clearTimeout(id);
  }, [phase]);

  if (phase === "wait" || phase === "gone") return null;
  return (
    <>
      {phase === "shown" ? <span aria-hidden="true" className="ady-hello-ring" /> : null}
      <span
        role="status"
        data-leaving={phase === "leaving" ? "" : undefined}
        className="ady-hello-chip pointer-events-none absolute -top-0.5 -right-1.5 z-30 rounded-full px-1.5 py-0.5 text-2xs leading-none font-bold"
      >
        {t("aiMascot.hello.title")}
      </span>
    </>
  );
}
